/**
 * Settings — Bot override
 *
 * Where a user connects their own Twitch account so PratBot speaks as it
 * instead of PratPerch, across every channel they own.
 *
 * This is a state machine, not a form. The states are derived from two API
 * reads (the caller's bots, and their current override) rather than tracked
 * locally, so a reload can never leave the page disagreeing with the server:
 *
 *   no_bot   no bot registered              -> instructions + Connect
 *   pending  just authorised, not confirmed -> Use this bot / Cancel
 *   idle     registered, not in use         -> Use this bot / Remove
 *   active   override set, token healthy    -> Disconnect
 *   broken   override set, token unusable   -> Reconnect / Remove
 *
 * `pending` and `idle` render the same shape and differ only in wording,
 * because they ARE the same server state — "you own a bot and it is not your
 * override". The distinction is whether it happened in this session, which is
 * the transient `justRegistered` id below. Deriving it any other way would
 * mean a second source of truth that goes stale the moment someone authorises
 * a bot and closes the tab.
 *
 * Registration deliberately does NOT set the override: the confirm step is the
 * last checkpoint against having authorised the wrong Twitch account.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import api, { ApiError, apiErrorMessage } from '@/api/client'
import botsApi, {
  isBotRegisteredMessage,
  openRegistrationPopup,
  type BotAccount,
  type BotModStatus,
} from '@/api/botsApi'
import { BotAvatar, Pill } from '@/components/BotStatus'
import { useToast } from '@/components/Toast'
import {
  AlertCircle,
  AlertTriangle,
  Bot,
  CheckCircle2,
  ExternalLink,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Trash2,
} from 'lucide-react'

/** Only the fields this page needs from GET /api/channels. */
interface ChannelSummary {
  id: number
  twitch_channel_name: string
  effective_bot_username: string | null
  effective_bot_is_override: boolean
}

type PageState = 'no_bot' | 'pending' | 'idle' | 'active' | 'broken'

/** How often to re-check for a new registration when postMessage never lands. */
const POLL_INTERVAL_MS = 2500
/** Give up polling after this long; the popup is presumably abandoned. */
const POLL_TIMEOUT_MS = 5 * 60 * 1000

export default function BotOverride() {
  const [systemBot, setSystemBot] = useState<BotAccount | null>(null)
  const [myBots, setMyBots] = useState<BotAccount[]>([])
  const [active, setActive] = useState<BotAccount | null>(null)
  const [channels, setChannels] = useState<ChannelSummary[]>([])
  /** Live Twitch mod state. null = not asked yet; see BotModStatus.checked. */
  const [modStatus, setModStatus] = useState<BotModStatus | null>(null)

  const [isLoading, setIsLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  /**
   * Load failure only. Every other outcome on this page -- authorisation,
   * override changes, removals -- is transient and goes to the toast queue;
   * before R15 they all shared this one slot, so a stale failure from a
   * click two minutes ago sat above a page that had since succeeded.
   */
  const [loadError, setLoadError] = useState<string | null>(null)
  const { success, error: errorToast, warning } = useToast()

  /** Bot authorised during THIS visit — the only thing separating pending from idle. */
  const [justRegistered, setJustRegistered] = useState<number | null>(null)
  const [awaitingPopup, setAwaitingPopup] = useState(false)

  const popupRef = useRef<Window | null>(null)
  const knownBotIds = useRef<Set<number>>(new Set())

  const load = useCallback(async () => {
    const [bots, activeBot, channelList] = await Promise.all([
      botsApi.list(),
      botsApi.getActive(),
      api.get<ChannelSummary[]>('/api/channels').catch(() => [] as ChannelSummary[]),
    ])

    // A super admin's /api/bots includes EVERY user's bots, so filter to the
    // ones this account actually owns — PUT /api/bots/active requires
    // ownership even for a super admin, and offering someone else's bot here
    // would only produce a 404.
    const mine = bots.user_bots.filter((b) => b.is_mine)

    setSystemBot(bots.system_bot)
    setMyBots(mine)
    setActive(activeBot)
    setChannels(channelList)

    // Mod status costs a Twitch round trip and only means anything for a
    // working override, so it is fetched separately and NOT awaited — the page
    // should not sit blank waiting on Helix.
    if (activeBot && activeBot.is_active && !activeBot.is_token_expired) {
      botsApi
        .getModStatus()
        .then(setModStatus)
        .catch(() => setModStatus(null))
    } else {
      setModStatus(null)
    }

    return mine
  }, [])

  useEffect(() => {
    load()
      .then((mine) => {
        knownBotIds.current = new Set(mine.map((b) => b.id))
      })
      .catch(() => setLoadError('Could not load your bot settings.'))
      .finally(() => setIsLoading(false))
  }, [load])

  // Listen for the popup's postMessage.
  //
  // event.origin is verified against our own origin — the callback posts to the
  // configured frontend origin, never "*", and a listener that skipped this
  // check would accept a bot_registered message from any page that can get a
  // handle on this window.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return
      if (!isBotRegisteredMessage(event.data)) return

      setAwaitingPopup(false)
      popupRef.current = null

      if (!event.data.ok) {
        errorToast(event.data.error || 'Twitch authorisation failed.')
        return
      }

      if (event.data.bot) setJustRegistered(event.data.bot.id)
      success(
        event.data.created === false
          ? 'Reconnected. The stored credentials have been replaced.'
          : `Authorised ${event.data.bot?.twitch_username || 'your bot'}.`,
      )
      // A refresh failure is a warning, not an error: the authorisation
      // itself landed, and reloading the page recovers the view.
      load().catch(() =>
        warning('Authorised, but the page could not refresh. Reload to see it.'),
      )
    }

    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
    // The toast functions are stable (useMemo in ToastProvider), so listing
    // them re-runs nothing; they are here so a future change to that
    // stability is caught by the dependency, not by a stale closure.
  }, [load, success, errorToast, warning])

  // Polling backstop for a popup whose window.opener was stripped (COOP) or
  // that opened as a tab, which is the common case on mobile Safari.
  //
  // It polls the BOT LIST, not /api/bots/active: registration creates the
  // account without setting the override, so /active would still be null and
  // the backstop would never fire. A new id appearing in the caller's own bots
  // is the actual signal.
  useEffect(() => {
    if (!awaitingPopup) return

    const startedAt = Date.now()
    const timer = window.setInterval(async () => {
      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        setAwaitingPopup(false)
        return
      }
      try {
        const mine = await load()
        const fresh = mine.find((b) => !knownBotIds.current.has(b.id))
        if (fresh) {
          knownBotIds.current = new Set(mine.map((b) => b.id))
          setJustRegistered(fresh.id)
          setAwaitingPopup(false)
        } else if (popupRef.current?.closed) {
          // Closed with nothing new registered: they cancelled, or the error
          // was shown inside the popup itself.
          setAwaitingPopup(false)
        }
      } catch {
        // Transient; the next tick tries again.
      }
    }, POLL_INTERVAL_MS)

    return () => window.clearInterval(timer)
  }, [awaitingPopup, load])

  /**
   * Launch the OAuth popup.
   *
   * NOTHING may be awaited before openRegistrationPopup() — popup blockers
   * only honour a window.open() still inside the user-gesture task. The popup
   * opens blank; startRegistration() then fetches its destination with an
   * authenticated POST (R22 — no JWT in the URL) and navigates it.
   */
  const handleConnect = () => {
    const popup = openRegistrationPopup()
    if (!popup) {
      errorToast(
        'Your browser blocked the Twitch sign-in window. Allow popups for this site and try again.',
      )
      return
    }
    popupRef.current = popup
    setAwaitingPopup(true)
    botsApi.startRegistration(popup).catch((err) => {
      setAwaitingPopup(false)
      errorToast(apiErrorMessage(err, 'Could not start the Twitch sign-in.'))
    })
  }

  const run = async (
    fn: () => Promise<unknown>,
    succeeded: string,
    failure: string,
  ) => {
    setBusy(true)
    try {
      await fn()
      await load()
      success(succeeded)
    } catch (err) {
      // A 404 here means the bot vanished under us — deleted from the admin
      // catalogue, or never owned by this account in the first place. That
      // case keeps its hand-written sentence because the API's own `detail`
      // for a 404 is deliberately indistinguishable from a permission denial
      // (no existence leak), which is correct on the wire and useless here.
      errorToast(
        err instanceof ApiError && err.status === 404
          ? `${failure} It may no longer exist — reload the page.`
          : apiErrorMessage(err, failure),
      )
    } finally {
      setBusy(false)
    }
  }

  const handleUse = (bot: BotAccount) =>
    run(
      async () => {
        await botsApi.setActive(bot.id)
        setJustRegistered(null)
      },
      `PratBot now speaks as ${bot.twitch_username} in your channels.`,
      'Could not set that bot as your override.',
    )

  const handleDisconnect = () =>
    run(
      async () => {
        await botsApi.clearActive()
        setJustRegistered(null)
      },
      `Override cleared. Your channels are served by ${systemBot?.twitch_username || 'the system bot'} again.`,
      'Could not disconnect. Your channels are unchanged.',
    )

  const handleRemove = (bot: BotAccount) => {
    if (
      !confirm(
        `Remove ${bot.twitch_username}?\n\nPratBot forgets the account and its stored Twitch credentials. Your channels revert to ${systemBot?.twitch_username || 'the system bot'}. You can connect it again later.`,
      )
    ) {
      return
    }
    run(
      async () => {
        await botsApi.remove(bot.id)
        setJustRegistered(null)
      },
      `Removed ${bot.twitch_username}.`,
      'Could not remove that bot account.',
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-synth-magenta"></div>
      </div>
    )
  }

  // ---- state derivation -------------------------------------------------
  const candidate = active || myBots.find((b) => b.id === justRegistered) || myBots[0] || null

  let state: PageState
  if (active) {
    state = active.is_active && !active.is_token_expired ? 'active' : 'broken'
  } else if (!candidate) {
    state = 'no_bot'
  } else {
    state = justRegistered === candidate.id ? 'pending' : 'idle'
  }

  const servedChannels = channels.filter((c) => c.effective_bot_is_override)
  const unmodded = (modStatus?.channels || []).filter((c) => !c.is_moderator)
  const systemName = systemBot?.twitch_username || 'PratPerch'

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Bot size={24} className="text-synth-magenta" />
          Bot override
        </h1>
        <p className="text-text-secondary text-sm mt-1">
          Have PratBot speak as your own Twitch account instead of {systemName},
          across every channel you own.
        </p>
      </div>

      {loadError && (
        <div className="bg-state-danger-bg border border-state-danger rounded-lg p-3 flex items-start gap-3">
          <AlertCircle className="text-state-danger shrink-0 mt-0.5" size={18} />
          <span className="text-state-danger text-sm">{loadError}</span>
        </div>
      )}

      <div className="card">
        {state === 'no_bot' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <BotAvatar name={systemName} tone="cyan" />
              <div>
                <p className="text-text-primary font-medium">{systemName}</p>
                <p className="text-text-secondary text-sm">
                  Serving your channels now
                </p>
              </div>
            </div>
            <p className="text-text-secondary text-sm leading-relaxed">
              Your channels are served by the shared PratBot account. Connect
              your own Twitch account and it will speak in their place — under
              its own name, with its own Twitch rate limit rather than one
              shared across every PratBot channel.
            </p>
            <div className="mt-4">
              <button
                onClick={handleConnect}
                className="btn-primary flex items-center gap-2"
                disabled={busy || awaitingPopup}
              >
                {awaitingPopup ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <ExternalLink size={16} />
                )}
                {awaitingPopup ? 'Waiting for Twitch…' : 'Connect a bot account'}
              </button>
            </div>
          </>
        )}

        {(state === 'pending' || state === 'idle') && candidate && (
          <>
            <div className="mb-3">
              {state === 'pending' ? (
                <Pill tone="warning">Awaiting confirmation</Pill>
              ) : (
                <Pill tone="muted">Not in use</Pill>
              )}
            </div>
            <div className="flex items-center gap-3 mb-4">
              <BotAvatar
                name={candidate.twitch_username}
                tone={state === 'pending' ? 'cyan' : 'muted'}
              />
              <div>
                <p className="text-text-primary font-medium">
                  {candidate.twitch_username}
                </p>
                <p className="text-text-muted text-xs">@{candidate.twitch_id}</p>
              </div>
            </div>
            <p className="text-text-secondary text-sm leading-relaxed">
              {state === 'pending' ? (
                <>
                  You authorised <strong>{candidate.twitch_username}</strong>.
                  Confirm and it replaces {systemName} in every channel you own.
                  Not the account you meant? Remove it and connect again from a
                  private window.
                </>
              ) : (
                <>
                  <strong>{candidate.twitch_username}</strong> is registered but
                  not in use — your channels are served by {systemName}.
                </>
              )}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                onClick={() => handleUse(candidate)}
                className="btn-primary"
                disabled={busy}
              >
                {busy ? 'Working…' : 'Use this bot'}
              </button>
              <button
                onClick={() => handleRemove(candidate)}
                className="btn-secondary flex items-center gap-2"
                disabled={busy}
              >
                <Trash2 size={16} />
                {state === 'pending' ? 'Cancel' : 'Remove bot'}
              </button>
            </div>
          </>
        )}

        {state === 'active' && active && (
          <>
            <div className="mb-3 flex flex-wrap gap-2">
              <Pill tone="mint">Active</Pill>
              <Pill tone="mint">Token healthy</Pill>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <BotAvatar name={active.twitch_username} tone="mint" />
              <div>
                <p className="text-text-primary font-medium">
                  {active.twitch_username}
                </p>
                <p className="text-text-muted text-xs">@{active.twitch_id}</p>
              </div>
            </div>

            <p className="text-text-secondary text-sm leading-relaxed">
              Speaking for every channel you own.
              {servedChannels.length > 0 && (
                <>
                  {' '}
                  Currently:{' '}
                  {servedChannels.map((c, i) => (
                    <span key={c.id}>
                      {i > 0 && ', '}
                      <Link
                        to={`/channels/${c.id}`}
                        className="text-synth-cyan hover:underline"
                      >
                        #{c.twitch_channel_name}
                      </Link>
                    </span>
                  ))}
                  .
                </>
              )}
            </p>

            {/* Applied live as of multi-bot item 7: setting the override
                deletes the outgoing bot's subscriptions and creates the
                incoming one's, in place. Best effort — if the bot process was
                down at the time, the change lands on its next start instead,
                so the copy promises "now" without promising it unconditionally. */}
            <div className="mt-4 bg-synth-mint/10 border border-synth-mint/40 rounded-lg p-3 flex items-start gap-3">
              <CheckCircle2 className="text-synth-mint shrink-0 mt-0.5" size={18} />
              <p className="text-synth-mint text-sm leading-relaxed">
                Live now. If the bot was offline when you connected it, the
                switch happens on its next start instead.
              </p>
            </div>

            {/* Mod status is live Twitch state and is sometimes UNKNOWABLE — a
                bot authorised before the scope existed cannot be asked. Three
                outcomes, not two: modded everywhere, specific channels missing,
                or could not ask. The last falls back to the generic reminder
                rather than claiming the bot is unmodded. */}
            {modStatus?.checked && unmodded.length === 0 && (
              <div className="mt-3 bg-synth-mint/10 border border-synth-mint/40 rounded-lg p-3 flex items-start gap-3">
                <ShieldCheck className="text-synth-mint shrink-0 mt-0.5" size={18} />
                <p className="text-synth-mint text-sm leading-relaxed">
                  Moderator in every channel it serves.
                </p>
              </div>
            )}

            {modStatus?.checked && unmodded.length > 0 && (
              <div className="mt-3 bg-state-warning/10 border border-state-warning/40 rounded-lg p-3 flex items-start gap-3">
                <ShieldAlert
                  className="text-state-warning shrink-0 mt-0.5"
                  size={18}
                />
                <p className="text-state-warning text-sm leading-relaxed">
                  Not a moderator in{' '}
                  {unmodded.map((c, i) => (
                    <span key={c.channel_id}>
                      {i > 0 && ', '}#{c.twitch_channel_name}
                    </span>
                  ))}
                  . Run{' '}
                  <code className="font-mono">/mod {active.twitch_username}</code>{' '}
                  there, or follow events stop for those channels and sends are
                  held to a much tighter rate.
                </p>
              </div>
            )}

            {!modStatus?.checked && (
              <div className="mt-3 bg-state-warning/10 border border-state-warning/40 rounded-lg p-3 flex items-start gap-3">
                <ShieldAlert
                  className="text-state-warning shrink-0 mt-0.5"
                  size={18}
                />
                <p className="text-state-warning text-sm leading-relaxed">
                  Run <code className="font-mono">/mod {active.twitch_username}</code>{' '}
                  in each of your channels. Without it, follow events stop and
                  the bot is held to a much tighter send rate.
                  {modStatus?.reason === 'lookup_failed' && (
                    <>
                      {' '}
                      Twitch would not say which channels it moderates —
                      reconnect the bot to enable that check.
                    </>
                  )}
                </p>
              </div>
            )}

            <div className="mt-4">
              <button
                onClick={handleDisconnect}
                className="btn-secondary"
                disabled={busy}
              >
                {busy ? 'Working…' : 'Disconnect'}
              </button>
            </div>
            <p className="text-text-muted text-xs mt-2">
              Disconnecting reverts your channels to {systemName}. The account
              stays registered, so you can switch back without re-authorising.
            </p>
          </>
        )}

        {state === 'broken' && active && (
          <>
            <div className="mb-3">
              <Pill tone="danger">
                {active.is_active ? 'Token expired' : 'Disabled'}
              </Pill>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <BotAvatar name={active.twitch_username} tone="danger" />
              <div>
                <p className="text-text-primary font-medium">
                  {active.twitch_username}
                </p>
                <p className="text-text-muted text-xs">@{active.twitch_id}</p>
              </div>
            </div>
            <p className="text-text-secondary text-sm leading-relaxed">
              This account can no longer sign in to Twitch — the stored
              credentials expired, or its access to PratBot was revoked. Your
              channels fall back to {systemName}.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {/* Reconnect re-runs OAuth, which replaces the tokens in place.
                  It is NOT POST /api/bots/{id}/refresh, which still 501s for
                  user bots. */}
              <button
                onClick={handleConnect}
                className="btn-primary flex items-center gap-2"
                disabled={busy || awaitingPopup}
              >
                {awaitingPopup ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <ExternalLink size={16} />
                )}
                {awaitingPopup ? 'Waiting for Twitch…' : 'Reconnect'}
              </button>
              <button
                onClick={() => handleRemove(active)}
                className="btn-secondary flex items-center gap-2"
                disabled={busy}
              >
                <Trash2 size={16} />
                Remove bot
              </button>
            </div>
            <p className="text-text-muted text-xs mt-2">
              Sign in as {active.twitch_username}, not your own account.
            </p>
          </>
        )}
      </div>

      {/* Walkthrough. Condensed from bot-account-setup.md — that page is the
          runbook and wins wherever the two disagree. */}
      <div className="card">
        <h2 className="text-lg font-semibold text-text-primary mb-3">
          Setting up a bot account
        </h2>

        <div className="bg-state-warning/10 border border-state-warning/40 rounded-lg p-3 flex items-start gap-3 mb-4">
          <AlertTriangle
            className="text-state-warning shrink-0 mt-0.5"
            size={18}
          />
          <p className="text-state-warning text-sm leading-relaxed">
            Sign out of Twitch, or open a private window, before you connect.
            The Twitch consent screen uses whoever is signed in — skip this and
            you'll authorise your <em>own</em> account, and it will start talking
            in your channel under your name.
          </p>
        </div>

        <ol className="list-decimal ml-5 space-y-2 text-sm text-text-secondary leading-relaxed">
          <li>
            Create a separate Twitch account for the bot, or use one you already
            have. Don't use your streaming account.
          </li>
          <li>
            Verify its email address. Twitch blocks chat from unverified
            accounts, and the failure is silent.
          </li>
          <li>
            Turn on two-factor authentication — Twitch requires it before an
            account can be made a moderator.
          </li>
          <li>Click Connect and sign in as the bot account.</li>
          <li>Check the account name on the Twitch screen before accepting.</li>
          <li>
            Confirm here, then run{' '}
            <code className="font-mono text-text-primary">/mod yourbotname</code>{' '}
            in each of your channels.
          </li>
        </ol>

        <p className="text-text-muted text-xs mt-4 leading-relaxed">
          Twitch rate-limits chat per bot account — 20 messages every 30 seconds,
          rising to 100 where the bot is a moderator. Every channel running on{' '}
          {systemName} draws from one shared allowance, which is the main reason
          to run your own.
        </p>
      </div>
    </div>
  )
}
